<script setup lang="ts">
import { onMounted, ref } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import { GpxMetersDistanceField } from '@gpx-spa/ui';
import { focusWithin } from '@/utils/focus';
import {
  PVT_SPINNER_STROKE_WIDTH,
  PVT_SPINNER_STYLE_COMPACT,
} from '@/constants/progress-spinner';
import {
  COMPOSER_CORRIDOR_BUFFER_MAX_M,
  COMPOSER_CORRIDOR_BUFFER_MIN_M,
  COMPOSER_CORRIDOR_BUFFER_STEP_M,
  migrateFormLocalStorageKey,
  SHIPMENT_COMPOSER_CORRIDOR_BUFFER_RECENT_KEY,
  SHIPMENT_COMPOSER_CORRIDOR_BUFFER_RECENT_KEY_LEGACY,
} from '@/components/shipment/form/domain/constants';

onMounted(() => {
  migrateFormLocalStorageKey(
    SHIPMENT_COMPOSER_CORRIDOR_BUFFER_RECENT_KEY,
    SHIPMENT_COMPOSER_CORRIDOR_BUFFER_RECENT_KEY_LEGACY,
  );
});

const props = defineProps<{
  showHeaderSpinner: boolean;
  pathIds: string[];
  pathGroups?: Array<{ title: string; pathIds: string[] }>;
  pathUi: Record<
    string,
    | {
        color: string;
        label: string;
        providerLabel?: string;
        segmentTitle?: string;
      }
    | undefined
  >;
  selectedByPathId: Record<string, boolean>;
  routingLoading: boolean;
  corridorBufferDebounceMs: number;
  commitCorridorBufferImmediately: () => void;
  /** When true, no outer card (parent provides Route preview container). */
  embedded?: boolean;
}>();

const corridorBufferM = defineModel<number>('corridorBufferM', {
  required: true,
});

const emit = defineEmits<{
  togglePath: [pathId: string, checked: boolean];
}>();

function routeTitle(pid: string, index: number) {
  return (
    props.pathUi[pid]?.providerLabel ??
    (index === 0 ? 'Primary' : `Alternative ${index}`)
  );
}

const sectionRootRef = ref<HTMLElement | null>(null);

function focusFirstRouteControl(): boolean {
  return focusWithin(sectionRootRef.value);
}

defineExpose({
  focusFirstRouteControl,
});
</script>

<template>
  <section
    ref="sectionRootRef"
    :class="
      embedded ? '' : 'rounded-lg border border-gray-200 bg-white p-6 shadow-sm'
    "
  >
    <div
      v-if="!embedded"
      class="flex min-h-10 items-center justify-between gap-3"
    >
      <h2 class="text-sm font-semibold text-gpx_black-200">Alternatives</h2>
      <div
        v-if="showHeaderSpinner"
        class="flex shrink-0 items-center justify-center overflow-visible"
        role="status"
        aria-busy="true"
        aria-label="Loading"
      >
        <ProgressSpinner
          :stroke-width="PVT_SPINNER_STROKE_WIDTH"
          :style="PVT_SPINNER_STYLE_COMPACT"
          aria-hidden="true"
        />
      </div>
    </div>
    <div
      v-else-if="showHeaderSpinner"
      class="flex min-h-8 items-center justify-end gap-3"
    >
      <div
        class="flex shrink-0 items-center justify-center overflow-visible"
        role="status"
        aria-busy="true"
        aria-label="Loading"
      >
        <ProgressSpinner
          :stroke-width="PVT_SPINNER_STROKE_WIDTH"
          :style="PVT_SPINNER_STYLE_COMPACT"
          aria-hidden="true"
        />
      </div>
    </div>
    <template v-if="pathIds.length">
      <div
        v-for="group in pathGroups?.length
          ? pathGroups
          : [{ title: '', pathIds }]"
        :key="group.title || 'routes'"
        class="mt-3"
      >
        <h3
          v-if="(pathGroups?.length ?? 0) > 1"
          class="mb-1.5 text-xs font-medium text-gpx_gray-100"
        >
          {{ group.title }}
        </h3>
        <ul class="space-y-1.5">
          <li v-for="(pid, idx) in group.pathIds" :key="pid">
            <label
              class="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors"
              :class="
                selectedByPathId[pid]
                  ? 'border-gpx_terracotta-100/30 bg-gpx_terracotta-900'
                  : 'border-gray-200 hover:bg-gray-50'
              "
            >
              <input
                :id="`path-${pid}`"
                type="checkbox"
                class="form-checkbox size-4 shrink-0 rounded border-gray-300 text-gpx_terracotta-100 focus:ring-gpx_terracotta-100"
                :checked="selectedByPathId[pid]"
                @change="
                  emit(
                    'togglePath',
                    pid,
                    ($event.target as HTMLInputElement).checked,
                  )
                "
              />
              <div
                class="h-0.5 w-4 shrink-0 rounded-full"
                :style="{ backgroundColor: pathUi[pid]?.color ?? '#9CA3AF' }"
              />
              <span class="text-sm text-gray-700">
                {{ routeTitle(pid, idx) }}
              </span>
              <span class="ml-auto text-xs tabular-nums text-gray-500">
                {{ pathUi[pid]?.label ?? '' }}
              </span>
            </label>
          </li>
        </ul>
      </div>

      <div class="mt-4">
        <GpxMetersDistanceField
          v-model="corridorBufferM"
          title="Corridor buffer"
          :min-meters="COMPOSER_CORRIDOR_BUFFER_MIN_M"
          :max-meters="COMPOSER_CORRIDOR_BUFFER_MAX_M"
          :step-meters="COMPOSER_CORRIDOR_BUFFER_STEP_M"
          size="small"
          :storage-key="SHIPMENT_COMPOSER_CORRIDOR_BUFFER_RECENT_KEY"
          recent-label="Recent"
          @slider-slide-end="commitCorridorBufferImmediately"
        />
        <p class="mt-1 text-[11px] text-gpx_gray-300">
          Heavy buffer geometry is debounced ({{ corridorBufferDebounceMs }}
          ms); releasing the distance slider applies immediately.
        </p>
      </div>
    </template>
    <p v-else-if="routingLoading" class="mt-3 text-xs text-gpx_gray-200">
      Fetching routes…
    </p>
  </section>
</template>
